import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
  Chip,
  Tooltip,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Menu,
  Autocomplete
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EventIcon from '@mui/icons-material/Event';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import PeopleIcon from '@mui/icons-material/People';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StarIcon from '@mui/icons-material/Star';
import {
  getWorkshopSettings,
  saveWorkshopSettings,
  uploadScopeItemsExcel,
  downloadScopeItemTemplate,
  getScopeItems,
  updateScopeItem,
  deleteScopeItem,
  clearAllScopeItems,
  replaceDuplicateScopeItems,
  getWorkshopNames,
  createWorkshopName,
  autoGenerateWorkshops,
  updateWorkshopName,
  deleteWorkshopName,
  moveWorkshopUp,
  moveWorkshopDown,
  getScopeMappings,
  createScopeMapping,
  updateScopeMapping,
  deleteScopeMapping,
  getModuleAttendees,
  addModuleAttendee,
  updateModuleAttendee,
  deleteModuleAttendee,
  generateWorkshops,
  getWorkshops,
  generateSchedule,
  getWorkshopSchedules,
  updateWorkshop,
  updateWorkshopSchedule,
  deleteWorkshopSchedule,
  createManualSchedule,
  generateAllCalendarInvites,
  getCalendarInvites,
  getDefaultWorkshopSettings,
  downloadICSFile,
  formatWorkshopDate,
  formatWorkshopTime,
  timezoneOptions,
  downloadScheduleExcel,
  uploadScheduleExcel,
  previewScheduleChanges,
  getProjectStakeholdersForPicker,
  syncStakeholdersToWorkshopModules
} from '../../utils/workshopService';
import SafetyConfirmDialog from '../../components/SafetyConfirmDialog';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const StepCard = styled(Card)(({ theme, completed }) => ({
  borderLeft: completed ? `4px solid ${theme.palette.success.main}` : `4px solid ${theme.palette.grey[300]}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[4]
  }
}));

const WorkshopSchedulePage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();

  // Main state
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Step 1: Workshop Settings
  const [settings, setSettings] = useState(getDefaultWorkshopSettings());
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Step 2: Scope Items
  const [scopeItems, setScopeItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Step 3: Workshop Names & Mappings
  const [workshopNames, setWorkshopNames] = useState([]);
  const [newWorkshopName, setNewWorkshopName] = useState('');
  const [scopeMappings, setScopeMappings] = useState({});  // scopeItemId -> workshopNameId (one-to-one mapping)
  const [workshopListExpanded, setWorkshopListExpanded] = useState(false);

  // Step 4: Module Attendees
  const [moduleAttendees, setModuleAttendees] = useState({});  // grouped by module
  const [mandatoryAttendees, setMandatoryAttendees] = useState([]);  // list of mandatory attendees
  const [newAttendee, setNewAttendee] = useState({ module: '', emails: [] });
  const [newMandatoryEmails, setNewMandatoryEmails] = useState([]);
  const [currentEmail, setCurrentEmail] = useState('');
  const [currentMandatoryEmail, setCurrentMandatoryEmail] = useState('');
  const [attendeeDialogOpen, setAttendeeDialogOpen] = useState(false);
  const [mandatoryAttendeeDialogOpen, setMandatoryAttendeeDialogOpen] = useState(false);
  const [projectStakeholders, setProjectStakeholders] = useState([]);
  const [stakeholdersLoading, setStakeholdersLoading] = useState(false);
  const [addingAttendees, setAddingAttendees] = useState(false);
  const [addingMandatoryAttendees, setAddingMandatoryAttendees] = useState(false);

  // Step 5 & 6: Workshops & Schedules
  const [generatedWorkshops, setGeneratedWorkshops] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [editScheduleDialogOpen, setEditScheduleDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [addScheduleDialogOpen, setAddScheduleDialogOpen] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    workshop_id: '',
    scheduled_date: '',
    start_time: '08:00',
    end_time: '10:00',
    time_zone: 'PST'
  });
  const [generating, setGenerating] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [calendarFormat, setCalendarFormat] = useState('google'); // google, outlook-web, outlook-ics, ics

  // Step 7: Calendar Invites
  const [calendarInvites, setCalendarInvites] = useState({});  // scheduleId -> invites
  const [generatingInvites, setGeneratingInvites] = useState(false);

  // Dialog states
  const [selectedFile, setSelectedFile] = useState(null);
  const [editWorkshopDialogOpen, setEditWorkshopDialogOpen] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [workshopNamesConfirmOpen, setWorkshopNamesConfirmOpen] = useState(false);
  const [scheduleConfirmOpen, setScheduleConfirmOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewChanges, setPreviewChanges] = useState([]);
  const [previewErrors, setPreviewErrors] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);

  // Expanded panels
  const [expandedPanel, setExpandedPanel] = useState(false);

  // Load initial data
  useEffect(() => {
    if (projectId) {
      loadInitialData();
    }
  }, [projectId]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Sync stakeholders to workshop modules (idempotent — handles pre-existing domain_module data)
      await syncStakeholdersToWorkshopModules(projectId).catch(() => {});

      // Load all data in parallel for better performance
      const [
        settingsData,
        scopeData,
        namesData,
        mappingsData,
        attendeesData,
        workshopsData,
        schedulesData
      ] = await Promise.all([
        getWorkshopSettings(projectId).catch(err => {
          console.error('Error loading settings:', err);
          return null;
        }),
        getScopeItems(projectId).catch(err => {
          console.error('Error loading scope items:', err);
          return [];
        }),
        getWorkshopNames(projectId).catch(err => {
          console.error('Error loading workshop names:', err);
          return [];
        }),
        getScopeMappings(projectId).catch(err => {
          console.error('Error loading scope mappings:', err);
          return {};
        }),
        getModuleAttendees(projectId).catch(err => {
          console.error('Error loading module attendees:', err);
          return {};
        }),
        getWorkshops(projectId).catch(err => {
          console.error('Error loading workshops:', err);
          return [];
        }),
        getWorkshopSchedules(projectId).catch(err => {
          console.error('Error loading schedules:', err);
          return [];
        })
      ]);

      // Process settings - only apply if real settings exist (has an id)
      if (settingsData && settingsData.id) {
        // Ensure time fields are in HH:MM format for HTML5 time inputs
        if (settingsData.allowed_start_time && settingsData.allowed_start_time.length > 5) {
          settingsData.allowed_start_time = settingsData.allowed_start_time.substring(0, 5);
        }
        if (settingsData.allowed_end_time && settingsData.allowed_end_time.length > 5) {
          settingsData.allowed_end_time = settingsData.allowed_end_time.substring(0, 5);
        }
        // Ensure exception_dates is always an array
        if (!Array.isArray(settingsData.exception_dates)) {
          settingsData.exception_dates = [];
        }
        setSettings(settingsData);
        setSettingsLoaded(true);
      }

      // Set all the loaded data
      setScopeItems(scopeData);
      setWorkshopNames(namesData);
      setScopeMappings(mappingsData);
      separateAttendees(attendeesData);

      if (workshopsData && workshopsData.length > 0) {
        setGeneratedWorkshops(workshopsData);
      }

      setSchedules(schedulesData);

    } catch (error) {
      console.error('Error loading initial data:', error);
      showSnackbar('Error loading workshop data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ==================== Handlers ====================

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  // Step 1: Settings
  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await saveWorkshopSettings(projectId, settings);
      setSettingsLoaded(true);
      showSnackbar('Workshop settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      showSnackbar('Error saving settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExceptionDate = (date) => {
    if (date && !settings.exception_dates?.includes(date)) {
      handleSettingsChange('exception_dates', [...settings.exception_dates, date]);
    }
  };

  const handleRemoveExceptionDate = (date) => {
    handleSettingsChange('exception_dates', settings.exception_dates.filter(d => d !== date));
  };

  // Step 2: Excel Upload
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        showSnackbar('Please select an Excel file (.xlsx or .xls)', 'error');
        return;
      }
      setSelectedFile(file);
    }
  };

  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [pendingDuplicates, setPendingDuplicates] = useState([]);
  const [selectedDuplicateIds, setSelectedDuplicateIds] = useState(new Set());

  const handleUploadExcel = async () => {
    if (!selectedFile) {
      showSnackbar('Please select a file first', 'warning');
      return;
    }

    setUploading(true);
    try {
      const result = await uploadScopeItemsExcel(projectId, selectedFile);
      setScopeItems(prev => [...prev, ...result.items]);
      setSelectedFile(null);
      document.getElementById('file-input').value = '';
      if (result.duplicates && result.duplicates.length > 0) {
        setPendingDuplicates(result.duplicates);
        setSelectedDuplicateIds(new Set(result.duplicates.map(d => d.existing_id)));
        setDuplicateDialogOpen(true);
        if (result.items.length > 0) {
          showSnackbar(result.message);
        }
      } else {
        showSnackbar(result.message);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showSnackbar(error.response?.data?.error || 'Error uploading file', 'error');
    } finally {
      setUploading(false);
    }
  };

  const toggleDuplicateSelection = (id) => {
    setSelectedDuplicateIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleReplaceSelected = async () => {
    const replacements = pendingDuplicates.filter(d => selectedDuplicateIds.has(d.existing_id));
    try {
      const result = await replaceDuplicateScopeItems(projectId, replacements);
      setScopeItems(prev => prev.map(item => {
        const updated = result.updated.find(u => u.id === item.id);
        return updated || item;
      }));
      showSnackbar(result.message);
    } catch (error) {
      showSnackbar('Error replacing scope items', 'error');
    } finally {
      setDuplicateDialogOpen(false);
      setPendingDuplicates([]);
      setSelectedDuplicateIds(new Set());
    }
  };

  const handleSkipDuplicates = () => {
    const skipped = pendingDuplicates.length;
    setDuplicateDialogOpen(false);
    setPendingDuplicates([]);
    setSelectedDuplicateIds(new Set());
    showSnackbar(`Skipped ${skipped} duplicate${skipped !== 1 ? 's' : ''}, existing data kept`);
  };

  const handleToggleWorkshopRequired = async (item) => {
    try {
      const updated = await updateScopeItem(item.id, {
        workshop_required: !item.workshop_required
      });
      setScopeItems(prev => prev.map(i => i.id === item.id ? updated : i));
    } catch (error) {
      console.error('Error updating scope item:', error);
      showSnackbar('Error updating scope item', 'error');
    }
  };

  const handleDeleteScopeItem = async (itemId) => {
    try {
      await deleteScopeItem(itemId);
      setScopeItems(prev => prev.filter(i => i.id !== itemId));
      showSnackbar('Scope item deleted');
    } catch (error) {
      console.error('Error deleting scope item:', error);
      showSnackbar('Error deleting scope item', 'error');
    }
  };

  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);

  const handleClearAll = () => setClearAllDialogOpen(true);

  const handleConfirmClearAll = async () => {
    setClearAllDialogOpen(false);
    try {
      await clearAllScopeItems(projectId);
      setScopeItems([]);
      showSnackbar('All scope items cleared');
    } catch (error) {
      console.error('Error clearing scope items:', error);
      showSnackbar('Error clearing scope items', 'error');
    }
  };

  // Step 3: Workshop Names & Mappings
  const handleCreateWorkshopName = async () => {
    if (!newWorkshopName.trim()) {
      showSnackbar('Please enter a workshop name', 'warning');
      return;
    }

    try {
      const created = await createWorkshopName(projectId, newWorkshopName);
      setWorkshopNames(prev => [...prev, created]);
      setNewWorkshopName('');
      showSnackbar('Workshop name created');
    } catch (error) {
      console.error('Error creating workshop name:', error);
      showSnackbar(error.response?.data?.error || 'Error creating workshop name', 'error');
    }
  };

  const handleAddNewWorkshop = async () => {
    try {
      // Find the next available workshop number
      const workshopNumbers = workshopNames
        .map(w => {
          const match = w.workshop_name.match(/Workshop (\d+)/);
          return match ? parseInt(match[1]) : 0;
        })
        .filter(n => n > 0);

      const nextNumber = workshopNumbers.length > 0 ? Math.max(...workshopNumbers) + 1 : workshopNames.length + 1;
      const newWorkshopId = `Workshop ${nextNumber}`;

      const created = await createWorkshopName(projectId, newWorkshopId);
      setWorkshopNames(prev => [...prev, created]);
      showSnackbar(`${newWorkshopId} created`);
    } catch (error) {
      console.error('Error creating workshop:', error);
      showSnackbar(error.response?.data?.error || 'Error creating workshop', 'error');
    }
  };

  const handleAutoGenerateWorkshops = async () => {
    setLoading(true);
    try {
      const result = await autoGenerateWorkshops(projectId);
      setWorkshopNames(result.workshops);
      // Merge auto-assigned mappings into state
      if (result.mappings) {
        setScopeMappings(result.mappings);
      }
      showSnackbar(result.message, 'success');

      // Auto-expand the mapping section after generation
      setExpandedPanel('mapping');
    } catch (error) {
      console.error('Error auto-generating workshops:', error);
      showSnackbar(error.response?.data?.error || 'Error generating workshops', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkshopMappingChange = async (scopeItemId, workshopNameId) => {
    try {
      // Use updateScopeMapping which handles both create and update
      await updateScopeMapping(scopeItemId, workshopNameId);

      // Update local state
      setScopeMappings(prev => ({
        ...prev,
        [scopeItemId]: {
          scope_item_id: scopeItemId,
          workshop_name_id: workshopNameId,
          mapping_id: prev[scopeItemId]?.mapping_id || null
        }
      }));

      showSnackbar('Workshop assignment updated');
    } catch (error) {
      console.error('Error updating workshop mapping:', error);
      showSnackbar(error.response?.data?.error || 'Error updating workshop assignment', 'error');
    }
  };

  const handleRemoveWorkshopMapping = async (scopeItemId) => {
    try {
      const mapping = scopeMappings[scopeItemId];
      if (mapping && mapping.mapping_id) {
        await deleteScopeMapping(mapping.mapping_id);
      }

      // Update local state
      setScopeMappings(prev => ({
        ...prev,
        [scopeItemId]: {
          scope_item_id: scopeItemId,
          workshop_name_id: null,
          mapping_id: null
        }
      }));

      showSnackbar('Workshop assignment removed');
    } catch (error) {
      console.error('Error removing workshop mapping:', error);
      showSnackbar('Error removing workshop assignment', 'error');
    }
  };

  const handleBulkAssignByModule = async (module, workshopNameId) => {
    setLoading(true);
    try {
      // Get all scope items for this module that are workshop required
      const moduleItems = scopeItems.filter(
        item => item.module === module && item.workshop_required
      );

      // Assign each item to the selected workshop
      const promises = moduleItems.map(item =>
        updateScopeMapping(item.id, workshopNameId)
      );

      await Promise.all(promises);

      // Update local state for all items
      const updatedMappings = { ...scopeMappings };
      moduleItems.forEach(item => {
        updatedMappings[item.id] = {
          scope_item_id: item.id,
          workshop_name_id: workshopNameId,
          mapping_id: updatedMappings[item.id]?.mapping_id || null
        };
      });
      setScopeMappings(updatedMappings);

      showSnackbar(`Assigned ${moduleItems.length} items from ${module} module`, 'success');
    } catch (error) {
      console.error('Error bulk assigning module:', error);
      showSnackbar('Error assigning module items', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorkshop = async (workshopId) => {
    try {
      await deleteWorkshopName(workshopId);

      // Remove from local state
      setWorkshopNames(prev => prev.filter(w => w.id !== workshopId));

      // Remove any mappings to this workshop from local state
      const updatedMappings = { ...scopeMappings };
      Object.keys(updatedMappings).forEach(scopeItemId => {
        if (updatedMappings[scopeItemId].workshop_name_id === workshopId) {
          updatedMappings[scopeItemId] = {
            scope_item_id: parseInt(scopeItemId),
            workshop_name_id: null,
            mapping_id: null
          };
        }
      });
      setScopeMappings(updatedMappings);

      showSnackbar('Workshop deleted successfully');
    } catch (error) {
      console.error('Error deleting workshop:', error);
      showSnackbar(error.response?.data?.error || 'Error deleting workshop', 'error');
    }
  };

  const handleMoveWorkshopUp = async (workshopId) => {
    try {
      await moveWorkshopUp(workshopId);

      // Refresh workshop names to get updated sequences
      const workshops = await getWorkshopNames(projectId);
      setWorkshopNames(workshops);

      // Also refresh generated workshops if they exist
      if (generatedWorkshops.length > 0) {
        const updatedGeneratedWorkshops = await getWorkshops(projectId);
        setGeneratedWorkshops(updatedGeneratedWorkshops);
      }

      showSnackbar('Workshop moved up successfully');
    } catch (error) {
      console.error('Error moving workshop up:', error);
      showSnackbar(error.response?.data?.error || 'Error moving workshop up', 'error');
    }
  };

  const handleMoveWorkshopDown = async (workshopId) => {
    try {
      await moveWorkshopDown(workshopId);

      // Refresh workshop names to get updated sequences
      const workshops = await getWorkshopNames(projectId);
      setWorkshopNames(workshops);

      // Also refresh generated workshops if they exist
      if (generatedWorkshops.length > 0) {
        const updatedGeneratedWorkshops = await getWorkshops(projectId);
        setGeneratedWorkshops(updatedGeneratedWorkshops);
      }

      showSnackbar('Workshop moved down successfully');
    } catch (error) {
      console.error('Error moving workshop down:', error);
      showSnackbar(error.response?.data?.error || 'Error moving workshop down', 'error');
    }
  };

  // Step 4: Attendees
  const handleOpenAttendeeDialog = (preSelectedModule = '') => {
    setNewAttendee({ module: preSelectedModule, emails: [] });
    setCurrentEmail('');
    setAttendeeDialogOpen(true);
    loadProjectStakeholders();
  };

  const handleAddEmailToList = () => {
    const raw = currentEmail.trim();
    if (!raw) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const tokens = raw.split(/[,;]/).map(e => e.trim()).filter(Boolean);
    const added = [];
    const invalid = [];
    const dupes = [];

    tokens.forEach(email => {
      if (!emailRegex.test(email)) { invalid.push(email); return; }
      if (newAttendee.emails.includes(email) || added.includes(email)) { dupes.push(email); return; }
      added.push(email);
    });

    const warnings = [];
    if (invalid.length) warnings.push(`Invalid: ${invalid.join(', ')}`);
    if (dupes.length) warnings.push(`Duplicate: ${dupes.join(', ')}`);
    if (warnings.length) showSnackbar(warnings.join(' | '), 'warning');

    if (added.length) {
      setNewAttendee(prev => ({ ...prev, emails: [...prev.emails, ...added] }));
      setCurrentEmail('');
    } else if (!warnings.length) {
      showSnackbar('No valid emails to add', 'warning');
    }
  };

  const handleRemoveEmailFromList = (emailToRemove) => {
    setNewAttendee(prev => ({
      ...prev,
      emails: prev.emails.filter(email => email !== emailToRemove)
    }));
  };

  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      handleAddEmailToList();
    }
  };

  const handleAddAttendees = async () => {
    if (!newAttendee.module) {
      showSnackbar('Please select a module', 'warning');
      return;
    }

    if (newAttendee.emails.length === 0) {
      showSnackbar('Please add at least one email address', 'warning');
      return;
    }

    setAddingAttendees(true);
    try {
      // Add all emails for the selected module (not mandatory)
      for (const email of newAttendee.emails) {
        await addModuleAttendee(projectId, newAttendee.module, email, '', false);
      }

      const attendeesData = await getModuleAttendees(projectId);
      separateAttendees(attendeesData);
      setAttendeeDialogOpen(false);
      setNewAttendee({ module: '', emails: [] });
      setCurrentEmail('');
      showSnackbar(`${newAttendee.emails.length} attendee(s) added successfully`);
    } catch (error) {
      console.error('Error adding attendees:', error);
      showSnackbar('Error adding attendees', 'error');
    } finally {
      setAddingAttendees(false);
    }
  };

  const handleDeleteAttendee = async (attendeeId) => {
    try {
      await deleteModuleAttendee(attendeeId);
      const attendeesData = await getModuleAttendees(projectId);
      separateAttendees(attendeesData);
      showSnackbar('Attendee removed');
    } catch (error) {
      console.error('Error deleting attendee:', error);
      showSnackbar('Error deleting attendee', 'error');
    }
  };

  // Mandatory attendees handlers
  const handleAddMandatoryAttendees = async () => {
    if (newMandatoryEmails.length === 0) {
      showSnackbar('Please add at least one email address', 'warning');
      return;
    }

    setAddingMandatoryAttendees(true);
    try {
      // Add all emails as mandatory attendees (using a dummy module)
      for (const email of newMandatoryEmails) {
        await addModuleAttendee(projectId, 'ALL', email, '', true);
      }

      const attendeesData = await getModuleAttendees(projectId);
      separateAttendees(attendeesData);
      setMandatoryAttendeeDialogOpen(false);
      setNewMandatoryEmails([]);
      setCurrentMandatoryEmail('');
      showSnackbar(`${newMandatoryEmails.length} mandatory attendee(s) added successfully`);
    } catch (error) {
      console.error('Error adding mandatory attendees:', error);
      showSnackbar('Error adding mandatory attendees', 'error');
    } finally {
      setAddingMandatoryAttendees(false);
    }
  };

  const handleDeleteMandatoryAttendee = async (attendeeId) => {
    try {
      await deleteModuleAttendee(attendeeId);
      const attendeesData = await getModuleAttendees(projectId);
      separateAttendees(attendeesData);
      showSnackbar('Mandatory attendee removed');
    } catch (error) {
      console.error('Error deleting mandatory attendee:', error);
      showSnackbar('Error deleting mandatory attendee', 'error');
    }
  };

  const handleAddMandatoryEmailToList = () => {
    const raw = currentMandatoryEmail.trim();
    if (!raw) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const tokens = raw.split(/[,;]/).map(e => e.trim()).filter(Boolean);
    const added = [];
    const invalid = [];
    const dupes = [];

    tokens.forEach(email => {
      if (!emailRegex.test(email)) { invalid.push(email); return; }
      if (newMandatoryEmails.includes(email) || added.includes(email)) { dupes.push(email); return; }
      added.push(email);
    });

    const warnings = [];
    if (invalid.length) warnings.push(`Invalid: ${invalid.join(', ')}`);
    if (dupes.length) warnings.push(`Duplicate: ${dupes.join(', ')}`);
    if (warnings.length) showSnackbar(warnings.join(' | '), 'warning');

    if (added.length) {
      setNewMandatoryEmails(prev => [...prev, ...added]);
      setCurrentMandatoryEmail('');
    } else if (!warnings.length) {
      showSnackbar('No valid emails to add', 'warning');
    }
  };

  const handleRemoveMandatoryEmailFromList = (emailToRemove) => {
    setNewMandatoryEmails(newMandatoryEmails.filter(email => email !== emailToRemove));
  };

  const handleMandatoryEmailKeyPress = (event) => {
    if ((event.key === 'Enter' || event.key === ',' || event.key === ';') && currentMandatoryEmail.trim()) {
      event.preventDefault();
      handleAddMandatoryEmailToList();
    }
  };

  const loadProjectStakeholders = async () => {
    if (projectStakeholders.length > 0) return;  // already loaded
    setStakeholdersLoading(true);
    try {
      const data = await getProjectStakeholdersForPicker(projectId);
      setProjectStakeholders(data);
    } catch (e) {
      console.error('Error loading stakeholders:', e);
    } finally {
      setStakeholdersLoading(false);
    }
  };

  // Step 5: Generate Workshops
  const handleGenerateWorkshops = async () => {
    setRegenerateDialogOpen(true);
  };

  const performGeneration = async () => {
    setGenerating(true);
    try {
      const result = await generateWorkshops(projectId);
      setGeneratedWorkshops(result.workshops);
      showSnackbar(result.message);
      setExpandedPanel('generated');
    } catch (error) {
      console.error('Error generating workshops:', error);
      showSnackbar(error.response?.data?.error || 'Error generating workshops', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirmRegenerate = async () => {
    setRegenerateDialogOpen(false);
    await performGeneration();
  };

  const handleOpenEditWorkshop = (workshop) => {
    setEditingWorkshop({
      ...workshop,
      agenda: [...workshop.agenda],
      materials_needed: [...workshop.materials_needed]
    });
    setEditWorkshopDialogOpen(true);
  };

  const handleSaveWorkshop = async () => {
    try {
      const result = await updateWorkshop(editingWorkshop.id, {
        objective: editingWorkshop.objective,
        agenda: editingWorkshop.agenda,
        materials_needed: editingWorkshop.materials_needed,
        notes: editingWorkshop.notes,
        attendees: editingWorkshop.attendees
      });

      // Refresh the full workshop list to get updated data
      const updatedWorkshops = await getWorkshops(projectId);
      setGeneratedWorkshops(updatedWorkshops);

      showSnackbar('Workshop updated successfully');
      setEditWorkshopDialogOpen(false);
    } catch (error) {
      console.error('Error updating workshop:', error);
      showSnackbar(error.response?.data?.error || 'Error updating workshop', 'error');
    }
  };

  // Step 6: Generate Schedule
  const handleGenerateSchedule = async () => {
    setScheduling(true);
    try {
      const result = await generateSchedule(projectId);
      setSchedules(result.schedules);
      showSnackbar(result.message);

      if (result.warnings && result.warnings.length > 0) {
        result.warnings.forEach(warning => {
          showSnackbar(warning, 'warning');
        });
      }

      setExpandedPanel('schedule');
    } catch (error) {
      console.error('Error generating schedule:', error);
      showSnackbar(error.response?.data?.error || 'Error generating schedule', 'error');
    } finally {
      setScheduling(false);
    }
  };

  // Edit Schedule Handlers
  const handleOpenEditSchedule = (schedule) => {
    setEditingSchedule({
      ...schedule,
      scheduled_date: schedule.scheduled_date,
      start_time: schedule.start_time.substring(0, 5), // Convert HH:MM:SS to HH:MM
      end_time: schedule.end_time.substring(0, 5)
    });
    setEditScheduleDialogOpen(true);
  };

  const handleCloseEditSchedule = () => {
    setEditScheduleDialogOpen(false);
    setEditingSchedule(null);
  };

  const handleSaveScheduleEdit = async () => {
    try {
      const updatedSchedule = await updateWorkshopSchedule(editingSchedule.id, {
        scheduled_date: editingSchedule.scheduled_date,
        start_time: editingSchedule.start_time,
        end_time: editingSchedule.end_time
      });

      // Update schedules in state
      setSchedules(schedules.map(s => s.id === updatedSchedule.id ? updatedSchedule : s));

      handleCloseEditSchedule();
      showSnackbar('Schedule updated successfully', 'success');
    } catch (error) {
      console.error('Error updating schedule:', error);
      showSnackbar(error.response?.data?.error || 'Error updating schedule', 'error');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      await deleteWorkshopSchedule(scheduleId);
      setSchedules(schedules.filter(s => s.id !== scheduleId));
      showSnackbar('Schedule deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting schedule:', error);
      showSnackbar(error.response?.data?.error || 'Error deleting schedule', 'error');
    }
  };

  const handleOpenAddSchedule = () => {
    setNewSchedule({
      workshop_id: generatedWorkshops[0]?.id || '',
      scheduled_date: new Date().toISOString().split('T')[0],
      start_time: '08:00',
      end_time: '10:00',
      time_zone: settings.time_zone || 'PST'
    });
    setAddScheduleDialogOpen(true);
  };

  const handleCloseAddSchedule = () => {
    setAddScheduleDialogOpen(false);
  };

  const handleSaveManualSchedule = async () => {
    try {
      const scheduleData = {
        ...newSchedule,
        project_id: parseInt(projectId)
      };

      const response = await createManualSchedule(scheduleData);
      setSchedules([...schedules, response.schedule]);
      handleCloseAddSchedule();
      showSnackbar('Schedule created successfully', 'success');
    } catch (error) {
      console.error('Error creating schedule:', error);
      showSnackbar(error.response?.data?.error || 'Error creating schedule', 'error');
    }
  };

  // Download and Upload Handlers
  const handleDownloadSchedule = async () => {
    try {
      setLoading(true);
      const result = await downloadScheduleExcel(projectId);
      showSnackbar(`Downloaded ${result.filename} successfully`, 'success');
    } catch (error) {
      console.error('Error downloading schedule:', error);
      showSnackbar(error.response?.data?.error || 'Error downloading schedule', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSchedule = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      // First, preview the changes
      const previewResult = await previewScheduleChanges(projectId, file);

      // Store the file and preview data
      setUploadFile(file);
      setPreviewChanges(previewResult.changes || []);
      setPreviewErrors(previewResult.errors || []);

      // Show preview dialog
      setPreviewDialogOpen(true);

      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Error previewing schedule:', error);
      showSnackbar(error.response?.data?.error || 'Error previewing schedule changes', 'error');
      event.target.value = '';
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!uploadFile) return;

    try {
      setLoading(true);
      setPreviewDialogOpen(false);

      const result = await uploadScheduleExcel(projectId, uploadFile);

      // Refresh schedules after upload
      const updatedSchedules = await getWorkshopSchedules(projectId);
      setSchedules(updatedSchedules);

      let message = result.message;
      if (result.errors && result.errors.length > 0) {
        message += `\n\nWarnings:\n${result.errors.join('\n')}`;
        showSnackbar(message, 'warning');
      } else {
        showSnackbar(message, 'success');
      }

      // Clear upload state
      setUploadFile(null);
      setPreviewChanges([]);
      setPreviewErrors([]);
    } catch (error) {
      console.error('Error uploading schedule:', error);
      showSnackbar(error.response?.data?.error || 'Error uploading schedule', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelUpload = () => {
    setPreviewDialogOpen(false);
    setUploadFile(null);
    setPreviewChanges([]);
    setPreviewErrors([]);
  };

  // Calendar Invite Handlers (Direct - No Backend Needed)
  const handleAddToCalendar = (schedule) => {
    const workshop = generatedWorkshops.find(w => w.workshop_name === schedule.workshop_name);

    // Build event details
    const title = schedule.workshop_name;
    let description = '';
    if (workshop) {
      const parts = [];
      if (workshop.objective) parts.push(`OBJECTIVE:\n${workshop.objective}`);
      if (workshop.scope_items_covered?.length > 0) {
        const scopeLines = workshop.scope_items_covered.map(s => `  [${s.module}] ${s.scope_item}`).join('\n');
        parts.push(`SCOPE ITEMS COVERED:\n${scopeLines}`);
      }
      if (workshop.agenda?.length > 0) {
        const agendaLines = workshop.agenda.map(item => `• ${item}`).join('\n');
        parts.push(`AGENDA:\n${agendaLines}`);
      }
      if (workshop.notes) parts.push(`NOTES:\n${workshop.notes}`);
      description = parts.join('\n\n');
    }
    const location = schedule.meeting_link || 'TBD';

    // Parse date and time
    const startDateTime = new Date(`${schedule.scheduled_date}T${schedule.start_time}`);
    const endDateTime = new Date(`${schedule.scheduled_date}T${schedule.end_time}`);

    // Get attendees
    const attendees = workshop?.attendees?.map(a => a.email).join(',') || '';

    switch (calendarFormat) {
      case 'google':
        openGoogleCalendar(title, description, location, startDateTime, endDateTime);
        break;
      case 'outlook-web':
        openOutlookWeb(title, description, location, startDateTime, endDateTime, attendees);
        break;
      case 'outlook-ics':
      case 'ics':
        downloadICSFile(schedule, workshop);
        break;
      default:
        showSnackbar('Please select a calendar format', 'warning');
    }
  };

  const openGoogleCalendar = (title, description, location, startDateTime, endDateTime) => {
    const formatGoogleDateTime = (date) => {
      return date.toISOString().replace(/-|:|\.\d\d\d/g, '');
    };

    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE` +
      `&text=${encodeURIComponent(title)}` +
      `&details=${encodeURIComponent(description)}` +
      `&location=${encodeURIComponent(location)}` +
      `&dates=${formatGoogleDateTime(startDateTime)}/${formatGoogleDateTime(endDateTime)}`;

    window.open(url, '_blank');
  };

  const openOutlookWeb = (title, description, location, startDateTime, endDateTime, attendees) => {
    const formatOutlookDateTime = (date) => {
      return date.toISOString();
    };

    // Outlook Web needs HTML in the body parameter for proper line break rendering
    const htmlBody = description
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/(OBJECTIVE:|SCOPE ITEMS COVERED:|AGENDA:|NOTES:)/g, '<b>$1</b>')
      .replace(/\n/g, '<br>');

    const url = `https://outlook.office.com/calendar/0/deeplink/compose?` +
      `subject=${encodeURIComponent(title)}` +
      `&body=${encodeURIComponent(htmlBody)}` +
      `&location=${encodeURIComponent(location)}` +
      `&startdt=${formatOutlookDateTime(startDateTime)}` +
      `&enddt=${formatOutlookDateTime(endDateTime)}` +
      `&to=${encodeURIComponent(attendees)}` +
      `&path=/calendar/action/compose&rru=addevent`;

    window.open(url, '_blank');
  };

  const downloadICSFile = async (schedule, workshop) => {
    try {
      // Generate ICS content client-side
      const title = schedule.workshop_name;
      let description = '';
      if (workshop) {
        const parts = [];
        if (workshop.objective) parts.push(`OBJECTIVE:\\n${workshop.objective}`);
        if (workshop.scope_items_covered?.length > 0) {
          const scopeLines = workshop.scope_items_covered.map(s => `  [${s.module}] ${s.scope_item}`).join('\\n');
          parts.push(`SCOPE ITEMS COVERED:\\n${scopeLines}`);
        }
        if (workshop.agenda?.length > 0) {
          const agendaLines = workshop.agenda.map(item => `\u2022 ${item}`).join('\\n');
          parts.push(`AGENDA:\\n${agendaLines}`);
        }
        if (workshop.notes) parts.push(`NOTES:\\n${workshop.notes}`);
        description = parts.join('\\n\\n');
      }
      const location = schedule.meeting_link || 'TBD';

      const startDateTime = new Date(`${schedule.scheduled_date}T${schedule.start_time}`);
      const endDateTime = new Date(`${schedule.scheduled_date}T${schedule.end_time}`);

      const formatICSDateTime = (date) => {
        return date.toISOString().replace(/-|:|\.\d\d\d/g, '');
      };

      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Workshop Scheduler//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `UID:${schedule.id}@workshopscheduler.com`,
        `DTSTAMP:${formatICSDateTime(new Date())}`,
        `DTSTART:${formatICSDateTime(startDateTime)}`,
        `DTEND:${formatICSDateTime(endDateTime)}`,
        `SUMMARY:${title}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${location}`,
        'STATUS:CONFIRMED',
        'SEQUENCE:0',
        'END:VEVENT',
        'END:VCALENDAR'
      ].join('\r\n');

      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${schedule.workshop_name.replace(/\s+/g, '_')}.ics`;
      link.click();

      showSnackbar('ICS file downloaded successfully', 'success');
    } catch (error) {
      console.error('Error generating ICS:', error);
      showSnackbar('Error generating ICS file', 'error');
    }
  };

  // Step 7: Generate Calendar Invites
  const handleGenerateAllInvites = async () => {
    setGeneratingInvites(true);
    try {
      const result = await generateAllCalendarInvites(projectId);
      showSnackbar(result.message);

      // Load invites for each schedule
      const invitesMap = {};
      for (const schedule of schedules) {
        try {
          const invites = await getCalendarInvites(schedule.id);
          invitesMap[schedule.id] = invites;
        } catch (err) {
          console.error(`Error loading invites for schedule ${schedule.id}:`, err);
        }
      }
      setCalendarInvites(invitesMap);

    } catch (error) {
      console.error('Error generating invites:', error);
      showSnackbar(error.response?.data?.error || 'Error generating calendar invites', 'error');
    } finally {
      setGeneratingInvites(false);
    }
  };

  const handleDownloadInvite = (scheduleId, platform) => {
    const invites = calendarInvites[scheduleId] || [];
    const invite = invites.find(inv => inv.platform === platform);

    if (!invite) {
      showSnackbar('Invite not found', 'error');
      return;
    }

    if (platform === 'ics') {
      const schedule = schedules.find(s => s.id === scheduleId);
      const blob = new Blob([invite.invite_data], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${(schedule?.workshop_name || 'workshop').replace(/\s+/g, '_')}.ics`;
      link.click();
      showSnackbar('ICS file downloaded successfully', 'success');
    } else if (platform === 'google') {
      window.open(invite.invite_data, '_blank');
    } else {
      // Copy JSON to clipboard
      navigator.clipboard.writeText(invite.invite_data);
      showSnackbar(`${platform.toUpperCase()} invite data copied to clipboard`);
    }
  };

  // Get unique modules from scope items
  const getUniqueModules = () => {
    return [...new Set(scopeItems.map(item => item.module))];
  };

  // Separate attendees into mandatory and module-specific
  const separateAttendees = (attendeesData) => {
    const moduleAttendeesMap = {};
    const mandatoryAttendeesList = [];

    // Group all attendees by module, but separate mandatory ones
    Object.keys(attendeesData).forEach(module => {
      const attendees = attendeesData[module] || [];
      moduleAttendeesMap[module] = attendees.filter(a => !a.is_mandatory);

      // Collect mandatory attendees (avoid duplicates by email)
      attendees.filter(a => a.is_mandatory).forEach(a => {
        if (!mandatoryAttendeesList.find(existing => existing.attendee_email === a.attendee_email)) {
          mandatoryAttendeesList.push(a);
        }
      });
    });

    setModuleAttendees(moduleAttendeesMap);
    setMandatoryAttendees(mandatoryAttendeesList);
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <SectionTitle variant="h5">
          <EventIcon />
          Workshop Schedule Configuration
        </SectionTitle>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure and schedule workshops for your SAP implementation project. Follow the steps below to set up workshop settings, upload scope items, map workshops, assign attendees, and generate the schedule.
        </Typography>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Step 1: Workshop Settings */}
        <Accordion
          expanded={expandedPanel === 'settings'}
          onChange={handleAccordionChange('settings')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SettingsIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Step 1: Workshop Settings</Typography>
              </Box>
              {settingsLoaded && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Configured"
                  color="success"
                  size="small"
                  sx={{ mr: 2 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Time Zone</InputLabel>
                  <Select
                    value={settings.time_zone}
                    onChange={(e) => handleSettingsChange('time_zone', e.target.value)}
                    label="Time Zone"
                  >
                    {timezoneOptions.map(tz => (
                      <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Workshop Duration (hours)"
                  type="number"
                  value={settings.duration_hours}
                  onChange={(e) => handleSettingsChange('duration_hours', parseFloat(e.target.value))}
                  inputProps={{ min: 0.5, max: 8, step: 0.5 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={settings.start_date}
                  onChange={(e) => handleSettingsChange('start_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ onClick: (e) => { try { e.target.showPicker(); } catch(err) {} } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={settings.end_date}
                  onChange={(e) => handleSettingsChange('end_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ onClick: (e) => { try { e.target.showPicker(); } catch(err) {} } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Allowed Start Time"
                  type="time"
                  value={settings.allowed_start_time}
                  onChange={(e) => handleSettingsChange('allowed_start_time', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ onClick: (e) => { try { e.target.showPicker(); } catch(err) {} } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Allowed End Time"
                  type="time"
                  value={settings.allowed_end_time}
                  onChange={(e) => handleSettingsChange('allowed_end_time', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ onClick: (e) => { try { e.target.showPicker(); } catch(err) {} } }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={settings.weekends_allowed}
                      onChange={(e) => handleSettingsChange('weekends_allowed', e.target.checked)}
                    />
                  }
                  label="Allow workshops on weekends"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={settings.allow_multiple_per_day}
                      onChange={(e) => handleSettingsChange('allow_multiple_per_day', e.target.checked)}
                    />
                  }
                  label="Allow multiple workshops scheduled in single day"
                />
              </Grid>

              {settings.allow_multiple_per_day && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Minimum Workshops Per Day"
                      type="number"
                      value={settings.min_workshops_per_day || ''}
                      onChange={(e) => handleSettingsChange('min_workshops_per_day', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="e.g., 1"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Maximum Workshops Per Day"
                      type="number"
                      value={settings.max_workshops_per_day || ''}
                      onChange={(e) => handleSettingsChange('max_workshops_per_day', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="e.g., 3"
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Exact Workshops Required"
                  type="number"
                  value={settings.exact_workshops_required || ''}
                  onChange={(e) => handleSettingsChange('exact_workshops_required', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="Leave empty for min/max"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Minimum Workshops"
                  type="number"
                  value={settings.min_workshops_required || ''}
                  onChange={(e) => handleSettingsChange('min_workshops_required', e.target.value ? parseInt(e.target.value) : null)}
                  disabled={!!settings.exact_workshops_required}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Maximum Workshops"
                  type="number"
                  value={settings.max_workshops_required || ''}
                  onChange={(e) => handleSettingsChange('max_workshops_required', e.target.value ? parseInt(e.target.value) : null)}
                  disabled={!!settings.exact_workshops_required}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Exception Dates (Holidays/Blocked Days)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    type="date"
                    label="Add Exception Date"
                    InputLabelProps={{ shrink: true }}
                    id="exception-date-input"
                    inputProps={{ onClick: (e) => { try { e.target.showPicker(); } catch(err) {} } }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const input = document.getElementById('exception-date-input');
                      if (input.value) {
                        handleAddExceptionDate(input.value);
                        input.value = '';
                      }
                    }}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {settings.exception_dates?.map((date, index) => (
                    <Chip
                      key={index}
                      label={formatWorkshopDate(date)}
                      onDelete={() => handleRemoveExceptionDate(date)}
                      color="error"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSettings}
                  disabled={loading}
                >
                  Save Settings
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Step 2: Upload Scope Items */}
        <Accordion
          expanded={expandedPanel === 'upload'}
          onChange={handleAccordionChange('upload')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <UploadFileIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Step 2: Upload Scope Items (Excel)</Typography>
              </Box>
              {scopeItems.length > 0 && (
                <Chip
                  label={`${scopeItems.length} items`}
                  color="primary"
                  size="small"
                  sx={{ mr: 2 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Upload an Excel file with columns: <strong>Module</strong>, <strong>Scope Item</strong>, <strong>Scope Item Description</strong>, and optionally <strong>Workshop Required</strong> (Yes/Y) and <strong>Workshop Number</strong> (e.g. 1, Workshop 1)
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Need the template?</Typography>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={downloadScopeItemTemplate}
                  >
                    Download Template
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    id="file-input"
                  />
                  <label htmlFor="file-input">
                    <Button variant="outlined" component="span">
                      Choose File
                    </Button>
                  </label>
                  {selectedFile && (
                    <Typography variant="body2">{selectedFile.name}</Typography>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<UploadFileIcon />}
                    onClick={handleUploadExcel}
                    disabled={!selectedFile || uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                  {scopeItems.length > 0 && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleClearAll}
                    >
                      Clear All
                    </Button>
                  )}
                </Box>
              </Grid>

              {scopeItems.length > 0 && (
                <Grid item xs={12}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Module</TableCell>
                          <TableCell>Scope Item</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="center">Workshop #</TableCell>
                          <TableCell align="center">Workshop Required</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scopeItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Chip label={item.module} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell>{item.scope_item}</TableCell>
                            <TableCell>{item.scope_item_description}</TableCell>
                            <TableCell align="center">
                              {item.workshop_hint ? (
                                <Chip label={`Workshop ${item.workshop_hint}`} size="small" color="secondary" />
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={item.workshop_required}
                                onChange={() => handleToggleWorkshopRequired(item)}
                                color="primary"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteScopeItem(item.id)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Step 3: Workshop Names & Mappings */}
        <Accordion
          expanded={expandedPanel === 'mapping'}
          onChange={handleAccordionChange('mapping')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MergeTypeIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Step 3: Map Scope Items to Workshops</Typography>
              </Box>
              {workshopNames.length > 0 && (
                <Chip
                  label={`${workshopNames.length} workshops`}
                  color="secondary"
                  size="small"
                  sx={{ mr: 2 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* Auto-generate workshops section */}
              <Grid item xs={12}>
                {workshopNames.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Smart Workshop Generation:</strong> The system will automatically create workshops based on your settings and scope items.
                    {' Click the button below to generate workshops.'}
                  </Alert>
                ) : (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <strong>{workshopNames.length} workshops created.</strong> You can regenerate workshops or manually add more below.
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => setWorkshopNamesConfirmOpen(true)}
                    disabled={scopeItems.filter(i => i.workshop_required).length === 0 || loading}
                    fullWidth
                  >
                    {loading ? 'Loading...' : workshopNames.length === 0 ? 'Generate Workshops' : 'Regenerate Workshops'}
                  </Button>
                </Box>

                {scopeItems.filter(i => i.workshop_required).length === 0 && (
                  <Alert severity="warning">
                    Please upload scope items and mark items as "Workshop Required" in Step 2 before generating workshops.
                  </Alert>
                )}
                {scopeItems.filter(i => i.workshop_required).length > 0 && !settingsLoaded && (
                  <Alert severity="warning">
                    Workshop settings have not been saved yet (Step 1). You can generate workshops now, but you will need to save settings before generating the schedule.
                  </Alert>
                )}
              </Grid>

              {/* Workshop List - Collapsible */}
              {workshopNames.length > 0 && (
                <Grid item xs={12}>
                  <Accordion
                    expanded={workshopListExpanded}
                    onChange={(e, isExpanded) => setWorkshopListExpanded(isExpanded)}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                        <Typography variant="subtitle2">
                          All Workshops ({workshopNames.length})
                        </Typography>
                        <Chip
                          label={`${Object.values(scopeMappings).filter(m => m.workshop_name_id).length} assigned`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="caption" color="text.secondary" gutterBottom display="block" sx={{ mb: 2 }}>
                        Note: Workshop IDs are temporary. You can rename workshops with meaningful names in Step 5 when generating workshop details.
                      </Typography>
                      <Grid container spacing={1}>
                        {workshopNames.map(workshop => {
                          const assignedCount = Object.values(scopeMappings).filter(
                            m => m.workshop_name_id === workshop.id
                          ).length;

                          return (
                            <Grid item xs={12} sm={6} md={4} key={workshop.id}>
                              <Box
                                sx={{
                                  p: 1.5,
                                  border: '1px solid',
                                  borderColor: assignedCount > 0 ? 'success.main' : 'grey.300',
                                  borderRadius: 1,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  bgcolor: assignedCount > 0 ? 'success.50' : 'background.paper'
                                }}
                              >
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {workshop.workshop_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {assignedCount} scope item{assignedCount !== 1 ? 's' : ''} assigned
                                  </Typography>
                                </Box>
                                <Tooltip title={assignedCount > 0 ? `Cannot delete. ${assignedCount} items assigned to this workshop.` : "Delete workshop"}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteWorkshop(workshop.id)}
                                      color="error"
                                      disabled={assignedCount > 0}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}

              {/* Simple button to add more workshops */}
              {workshopNames.length > 0 && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddNewWorkshop}
                    size="small"
                  >
                    Add Another Workshop
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    Creates a new workshop with the next available ID (will be renamed in Step 5)
                  </Typography>
                </Grid>
              )}

              {workshopNames.length > 0 && scopeItems.filter(i => i.workshop_required).length > 0 ? (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Business Rule:</strong> Each scope item can only be assigned to ONE workshop. Use the dropdown to select which workshop each scope item belongs to.
                  </Alert>

                  {/* Bulk Assignment by Module */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AutoAwesomeIcon fontSize="small" />
                      Quick Assign: Assign All Items from a Module
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {Array.from(new Set(scopeItems.filter(i => i.workshop_required).map(i => i.module))).map(module => {
                        const moduleItemCount = scopeItems.filter(i => i.module === module && i.workshop_required).length;
                        return (
                          <Grid item xs={12} sm={6} md={4} key={module}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Chip
                                label={`${module} (${moduleItemCount})`}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />
                              <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                                <Select
                                  displayEmpty
                                  onChange={(e) => handleBulkAssignByModule(module, e.target.value)}
                                  value=""
                                >
                                  <MenuItem value="" disabled>
                                    <em>Assign to...</em>
                                  </MenuItem>
                                  {workshopNames.map(workshop => (
                                    <MenuItem key={workshop.id} value={workshop.id}>
                                      {workshop.workshop_name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Paper>

                  <Typography variant="subtitle2" gutterBottom>
                    Individual Assignments:
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell width="35%">Scope Item</TableCell>
                          <TableCell width="15%">Module</TableCell>
                          <TableCell width="40%">Assigned Workshop</TableCell>
                          <TableCell width="10%" align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scopeItems.filter(item => item.workshop_required).map((item) => {
                          const assignedWorkshopId = scopeMappings[item.id]?.workshop_name_id;
                          return (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2">{item.scope_item}</Typography>
                                  {item.workshop_hint && (
                                    <Chip label={`W${item.workshop_hint}`} size="small" color="secondary" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                                  )}
                                </Box>
                                {item.scope_item_description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {item.scope_item_description.substring(0, 100)}
                                    {item.scope_item_description.length > 100 ? '...' : ''}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip label={item.module} size="small" color="primary" variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={assignedWorkshopId || ''}
                                    onChange={(e) => handleWorkshopMappingChange(item.id, e.target.value)}
                                    displayEmpty
                                    sx={{ minWidth: 200 }}
                                  >
                                    <MenuItem value="" disabled>
                                      <em>Select a workshop...</em>
                                    </MenuItem>
                                    {workshopNames.map(workshop => (
                                      <MenuItem key={workshop.id} value={workshop.id}>
                                        {workshop.workshop_name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell align="center">
                                {assignedWorkshopId && (
                                  <Tooltip title="Remove assignment">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveWorkshopMapping(item.id)}
                                      color="error"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Show summary */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Mapping Summary:
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Chip
                          label={`${scopeItems.filter(i => i.workshop_required).length} Total Scope Items`}
                          color="default"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Chip
                          label={`${Object.values(scopeMappings).filter(m => m.workshop_name_id).length} Assigned`}
                          color="success"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Chip
                          label={`${scopeItems.filter(i => i.workshop_required).length - Object.values(scopeMappings).filter(m => m.workshop_name_id).length} Unassigned`}
                          color="warning"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Please generate workshops and mark scope items as "Workshop Required" before mapping.
                  </Alert>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Step 4: Module Attendees */}
        <Accordion
          expanded={expandedPanel === 'attendees'}
          onChange={handleAccordionChange('attendees')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Step 4: Assign Module Attendees</Typography>
              </Box>
              {Object.keys(moduleAttendees).length > 0 && (
                <Chip
                  label={`${Object.values(moduleAttendees).flat().length} attendees`}
                  color="info"
                  size="small"
                  sx={{ mr: 2 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* Mandatory Attendees Section */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ bgcolor: 'warning.50', borderColor: 'warning.main', borderWidth: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StarIcon color="warning" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="h6" color="warning.dark">
                            Mandatory Attendees
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            These attendees will be included in ALL workshops regardless of conflicts
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        color="warning"
                        startIcon={<AddIcon />}
                        onClick={() => { setMandatoryAttendeeDialogOpen(true); loadProjectStakeholders(); }}
                      >
                        Add Mandatory Attendee
                      </Button>
                    </Box>
                    {mandatoryAttendees.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {mandatoryAttendees.map((attendee) => (
                          <Chip
                            key={attendee.id}
                            icon={<StarIcon />}
                            label={attendee.attendee_email}
                            onDelete={() => handleDeleteMandatoryAttendee(attendee.id)}
                            color="warning"
                            variant="filled"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Alert severity="info">
                        No mandatory attendees added yet. Mandatory attendees will supervise all workshops.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Module-Specific Attendees Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Module-Specific Attendees
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Assign attendees to specific modules. They will only attend workshops related to their modules.
                </Typography>
              </Grid>

              {getUniqueModules().map(module => (
                <Grid item xs={12} key={module}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" color="primary">
                          {module}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => handleOpenAttendeeDialog(module)}
                        >
                          Add
                        </Button>
                      </Box>
                      {(moduleAttendees[module] || []).length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {(moduleAttendees[module] || []).map((attendee) => (
                            <Chip
                              key={attendee.id}
                              label={attendee.attendee_email}
                              onDelete={() => handleDeleteAttendee(attendee.id)}
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No attendees assigned to this module yet
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Step 5: Generate Workshops */}
        <Accordion
          expanded={expandedPanel === 'generated'}
          onChange={handleAccordionChange('generated')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AutoAwesomeIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Step 5: Generate Workshop Content</Typography>
              </Box>
              {generatedWorkshops.length > 0 && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Generated"
                  color="success"
                  size="small"
                  sx={{ mr: 2 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This will generate workshop objectives, agendas, materials needed, and notes based on your scope items and module mappings.
                </Alert>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleGenerateWorkshops}
                  disabled={generating || workshopNames.length === 0}
                >
                  {generating ? 'Generating...' :
                   generatedWorkshops.length > 0 ? 'Regenerate Workshop Content' :
                   'Generate Workshop Content'}
                </Button>
              </Grid>

              {generatedWorkshops.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Generated Workshops:
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Use the up/down arrows to set the sequence in which workshops should be scheduled.
                  </Alert>
                  {generatedWorkshops.map((workshop, index) => {
                    const isFirst = index === 0;
                    const isLast = index === generatedWorkshops.length - 1;

                    return (
                    <Card key={workshop.id} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" color="primary">
                              {workshop.workshop_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Workshop #{workshop.sequence || index + 1}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Move up in sequence">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleMoveWorkshopUp(workshop.workshop_name_id)}
                                  disabled={isFirst}
                                >
                                  <ArrowUpwardIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Move down in sequence">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleMoveWorkshopDown(workshop.workshop_name_id)}
                                  disabled={isLast}
                                >
                                  <ArrowDownwardIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEditWorkshop(workshop)}
                              title="Edit workshop"
                            >
                              <EditIcon />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Objective:
                          </Typography>
                          <Typography variant="body2" paragraph>
                            {workshop.objective}
                          </Typography>
                        </Box>

                        {workshop.scope_items_covered?.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Scope Items Covered:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {workshop.scope_items_covered.map((item, i) => (
                                <Tooltip key={i} title={item.description || ''} placement="top" arrow>
                                  <Chip
                                    label={item.scope_item}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    icon={<Chip component="span" label={item.module} size="small" sx={{ fontSize: '0.6rem', height: 16, mr: -0.5, bgcolor: 'primary.light', border: 'none', '& .MuiChip-label': { color: '#000000' } }} />}
                                    sx={{ maxWidth: 280 }}
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          </Box>
                        )}

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Agenda:
                            </Typography>
                            <List dense>
                              {workshop.agenda?.map((item, i) => (
                                <ListItem key={i}>
                                  <ListItemText primary={`${i + 1}. ${item}`} />
                                </ListItem>
                              ))}
                            </List>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Materials Needed:
                            </Typography>
                            <List dense>
                              {workshop.materials_needed?.map((item, i) => (
                                <ListItem key={i}>
                                  <ListItemText primary={`• ${item}`} />
                                </ListItem>
                              ))}
                            </List>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Modules Involved:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                              {workshop.modules_involved?.map((module, i) => (
                                <Chip key={i} label={module} size="small" color="primary" />
                              ))}
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Attendees ({workshop.attendees?.length || 0}):
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                              {workshop.attendees?.map((attendee, i) => (
                                <Chip
                                  key={i}
                                  label={`${attendee.name || attendee.email} (${attendee.module})`}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Grid>

                          {workshop.notes && (
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Notes:
                              </Typography>
                              <Typography variant="body2">
                                {workshop.notes}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                    );
                  })}
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Step 6: Generate Schedule */}
        <Accordion
          expanded={expandedPanel === 'schedule'}
          onChange={handleAccordionChange('schedule')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarMonthIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Step 6: Generate Workshop Schedule</Typography>
              </Box>
              {schedules.length > 0 && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${schedules.length} scheduled`}
                  color="success"
                  size="small"
                  sx={{ mr: 2 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  The system will automatically schedule workshops while avoiding attendee conflicts and respecting all configured constraints.
                </Alert>

                {/* Calendar Format Selector */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Calendar Format:
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={calendarFormat}
                      onChange={(e) => setCalendarFormat(e.target.value)}
                    >
                      <FormControlLabel
                        value="google"
                        control={<Radio />}
                        label="Google Calendar"
                      />
                      <FormControlLabel
                        value="outlook-web"
                        control={<Radio />}
                        label="Outlook Web (New)"
                      />
                      <FormControlLabel
                        value="outlook-ics"
                        control={<Radio />}
                        label="Outlook Desktop (ICS)"
                      />
                      <FormControlLabel
                        value="ics"
                        control={<Radio />}
                        label="ICS File (Universal)"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CalendarMonthIcon />}
                    onClick={() => setScheduleConfirmOpen(true)}
                    disabled={scheduling || generatedWorkshops.length === 0 || !settingsLoaded}
                  >
                    {scheduling ? 'Scheduling...' : 'Generate Schedule'}
                  </Button>

                  {schedules.length > 0 && (
                    <>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadSchedule}
                        disabled={loading}
                      >
                        Download Excel
                      </Button>

                      <Button
                        variant="contained"
                        color="warning"
                        component="label"
                        startIcon={<UploadFileIcon />}
                        disabled={loading}
                      >
                        Upload Excel
                        <input
                          type="file"
                          hidden
                          accept=".xlsx,.xls"
                          onChange={handleUploadSchedule}
                        />
                      </Button>
                    </>
                  )}
                </Box>
              </Grid>

              {schedules.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Workshop Schedule:
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAddSchedule}
                      disabled={generatedWorkshops.length === 0}
                    >
                      Add Manual Schedule
                    </Button>
                  </Box>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Workshop Name</TableCell>
                          <TableCell>Attendees</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Time</TableCell>
                          <TableCell>Time Zone</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schedules.map((schedule) => {
                          // Find the corresponding workshop to get attendees
                          const workshop = generatedWorkshops.find(w => w.workshop_name === schedule.workshop_name);
                          const attendees = workshop?.attendees || [];

                          return (
                            <TableRow key={schedule.id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {schedule.workshop_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Workshop #{schedule.workshop_sequence || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 300 }}>
                                  {attendees.length > 0 ? (
                                    attendees.map((attendee, i) => (
                                      <Chip
                                        key={i}
                                        label={attendee.name || attendee.email}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                      />
                                    ))
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      No attendees
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={<EventIcon />}
                                  label={formatWorkshopDate(schedule.scheduled_date)}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                            <TableCell>
                              <Chip
                                icon={<AccessTimeIcon />}
                                label={`${formatWorkshopTime(schedule.start_time)} - ${formatWorkshopTime(schedule.end_time)}`}
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                                  '& .MuiChip-label': {
                                    color: 'text.primary'
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>{schedule.time_zone}</TableCell>
                            <TableCell>
                              <Chip
                                label={schedule.status}
                                size="small"
                                color={schedule.status === 'scheduled' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Edit Schedule">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenEditSchedule(schedule)}
                                    color="primary"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Schedule">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteSchedule(schedule.id)}
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={`Add to ${calendarFormat === 'google' ? 'Google Calendar' : calendarFormat === 'outlook-web' ? 'Outlook Web' : 'Calendar'}`}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleAddToCalendar(schedule)}
                                    startIcon={<EventIcon />}
                                  >
                                    Add to Calendar
                                  </Button>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Step 7: Calendar Invites */}
        <Accordion
          expanded={expandedPanel === 'invites'}
          onChange={handleAccordionChange('invites')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DownloadIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Step 7: Download Calendar Invites</Typography>
              </Box>
              {Object.keys(calendarInvites).length > 0 && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Generated"
                  color="success"
                  size="small"
                  sx={{ mr: 2 }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Existing calendar invite files will be refreshed using the latest workshop schedule and attendee details.
                  This action does not use AI.
                </Alert>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleGenerateAllInvites}
                  disabled={generatingInvites || schedules.length === 0}
                >
                  {generatingInvites ? 'Generating...' : 'Generate All Calendar Invites'}
                </Button>
              </Grid>

              {Object.keys(calendarInvites).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Download Invites:
                  </Typography>
                  {schedules.map((schedule) => (
                    <Card key={schedule.id} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {schedule.workshop_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Workshop #{schedule.workshop_sequence || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {formatWorkshopDate(schedule.scheduled_date)} • {formatWorkshopTime(schedule.start_time)} - {formatWorkshopTime(schedule.end_time)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleDownloadInvite(schedule.id, 'ics')}
                          >
                            Download ICS
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleDownloadInvite(schedule.id, 'google')}
                          >
                            Google Calendar
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleDownloadInvite(schedule.id, 'teams')}
                          >
                            Teams (Copy JSON)
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleDownloadInvite(schedule.id, 'slack')}
                          >
                            Slack (Copy JSON)
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleDownloadInvite(schedule.id, 'outlook')}
                          >
                            Outlook (Copy JSON)
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Add Attendee Dialog */}
      <Dialog open={attendeeDialogOpen} onClose={() => setAttendeeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Module Attendees</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Module</InputLabel>
                <Select
                  value={newAttendee.module}
                  onChange={(e) => setNewAttendee(prev => ({ ...prev, module: e.target.value }))}
                  label="Module"
                >
                  {getUniqueModules().map(module => (
                    <MenuItem key={module} value={module}>{module}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={projectStakeholders.filter(s => s.main_email)}
                loading={stakeholdersLoading}
                getOptionLabel={(s) => `${s.name}${s.role ? ` — ${s.role}` : ''} <${s.main_email}>`}
                isOptionEqualToValue={(a, b) => a.stakeholder_id === b.stakeholder_id}
                value={projectStakeholders.filter(s => s.main_email && newAttendee.emails.includes(s.main_email))}
                onChange={(_, selected) => {
                  const selectedEmails = selected.map(s => s.main_email).filter(Boolean);
                  const nonStakeholderEmails = newAttendee.emails.filter(
                    e => !projectStakeholders.some(s => s.main_email === e)
                  );
                  setNewAttendee(prev => ({ ...prev, emails: [...new Set([...nonStakeholderEmails, ...selectedEmails])] }));
                }}
                disabled={!newAttendee.module}
                renderInput={(params) => (
                  <TextField {...params} label="Select from Stakeholders" placeholder="Search by name, role or email" />
                )}
                noOptionsText={projectStakeholders.length === 0 ? 'No stakeholders found for this project' : 'No match'}
                sx={{ mb: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label="Or type email manually"
                  type="email"
                  placeholder="Enter one or more emails separated by comma or semicolon"
                  value={currentEmail}
                  onChange={(e) => setCurrentEmail(e.target.value)}
                  onKeyPress={handleEmailKeyPress}
                  disabled={!newAttendee.module}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddEmailToList}
                  disabled={!newAttendee.module || !currentEmail.trim()}
                  sx={{ minWidth: '80px' }}
                >
                  Add
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Enter a single email or multiple emails separated by comma (,) or semicolon (;), then press Enter or click Add
              </Typography>
            </Grid>

            {newAttendee.emails.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="subtitle2">
                    Added Emails ({newAttendee.emails.length}):
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setNewAttendee(prev => ({ ...prev, emails: [] }))}
                  >
                    Remove All
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {newAttendee.emails.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      onDelete={() => handleRemoveEmailFromList(email)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttendeeDialogOpen(false)} disabled={addingAttendees}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddAttendees}
            disabled={!newAttendee.module || newAttendee.emails.length === 0 || addingAttendees}
            startIcon={addingAttendees ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {addingAttendees ? 'Adding...' : `Add${newAttendee.emails.length > 0 ? ` (${newAttendee.emails.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Mandatory Attendees Dialog */}
      <Dialog open={mandatoryAttendeeDialogOpen} onClose={() => setMandatoryAttendeeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StarIcon color="warning" sx={{ mr: 1 }} />
            Add Mandatory Attendees
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Mandatory attendees will be included in ALL workshops regardless of modules or scheduling conflicts. Use this for project managers or supervisors who need to attend every session.
          </Alert>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={projectStakeholders.filter(s => s.main_email)}
                loading={stakeholdersLoading}
                getOptionLabel={(s) => `${s.name}${s.role ? ` — ${s.role}` : ''} <${s.main_email}>`}
                isOptionEqualToValue={(a, b) => a.stakeholder_id === b.stakeholder_id}
                value={projectStakeholders.filter(s => s.main_email && newMandatoryEmails.includes(s.main_email))}
                onChange={(_, selected) => {
                  const selectedEmails = selected.map(s => s.main_email).filter(Boolean);
                  const nonStakeholderEmails = newMandatoryEmails.filter(
                    e => !projectStakeholders.some(s => s.main_email === e)
                  );
                  setNewMandatoryEmails([...new Set([...nonStakeholderEmails, ...selectedEmails])]);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select from Stakeholders" placeholder="Search by name, role or email" />
                )}
                noOptionsText={projectStakeholders.length === 0 ? 'No stakeholders found for this project' : 'No match'}
                sx={{ mb: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label="Or type email manually"
                  type="email"
                  placeholder="Enter one or more emails separated by comma or semicolon"
                  value={currentMandatoryEmail}
                  onChange={(e) => setCurrentMandatoryEmail(e.target.value)}
                  onKeyPress={handleMandatoryEmailKeyPress}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddMandatoryEmailToList}
                  disabled={!currentMandatoryEmail.trim()}
                  sx={{ minWidth: '80px' }}
                >
                  Add
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Enter a single email or multiple emails separated by comma (,) or semicolon (;), then press Enter or click Add
              </Typography>
            </Grid>

            {newMandatoryEmails.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="subtitle2">
                    Added Emails ({newMandatoryEmails.length}):
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setNewMandatoryEmails([])}
                  >
                    Remove All
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {newMandatoryEmails.map((email, index) => (
                    <Chip
                      key={index}
                      icon={<StarIcon />}
                      label={email}
                      onDelete={() => handleRemoveMandatoryEmailFromList(email)}
                      color="warning"
                      variant="filled"
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMandatoryAttendeeDialogOpen(false)} disabled={addingMandatoryAttendees}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleAddMandatoryAttendees}
            disabled={newMandatoryEmails.length === 0 || addingMandatoryAttendees}
            startIcon={addingMandatoryAttendees ? <CircularProgress size={16} color="inherit" /> : <StarIcon />}
          >
            {addingMandatoryAttendees ? 'Adding...' : `Add Mandatory${newMandatoryEmails.length > 0 ? ` (${newMandatoryEmails.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Workshop Dialog */}
      {editingWorkshop && (
        <Dialog
          open={editWorkshopDialogOpen}
          onClose={() => setEditWorkshopDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Workshop: {editingWorkshop.workshop_name}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              {/* Objective */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Objective"
                  value={editingWorkshop.objective || ''}
                  onChange={(e) => setEditingWorkshop({ ...editingWorkshop, objective: e.target.value })}
                />
              </Grid>

              {/* Scope Items Covered (read-only — driven by Step 3 mappings) */}
              {editingWorkshop.scope_items_covered?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Scope Items Covered:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    {editingWorkshop.scope_items_covered.map((item, i) => (
                      <Tooltip key={i} title={item.description || ''} placement="top" arrow>
                        <Chip label={item.scope_item} size="small" color="primary" variant="outlined" />
                      </Tooltip>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    To change scope item assignments, update the mappings in Step 3.
                  </Typography>
                </Grid>
              )}

              {/* Agenda */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Agenda Items:</Typography>
                {editingWorkshop.agenda && editingWorkshop.agenda.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={item}
                      onChange={(e) => {
                        const newAgenda = [...editingWorkshop.agenda];
                        newAgenda[index] = e.target.value;
                        setEditingWorkshop({ ...editingWorkshop, agenda: newAgenda });
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newAgenda = editingWorkshop.agenda.filter((_, i) => i !== index);
                        setEditingWorkshop({ ...editingWorkshop, agenda: newAgenda });
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingWorkshop({
                      ...editingWorkshop,
                      agenda: [...(editingWorkshop.agenda || []), '']
                    });
                  }}
                >
                  Add Agenda Item
                </Button>
              </Grid>

              {/* Materials Needed */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Materials Needed:</Typography>
                {editingWorkshop.materials_needed && editingWorkshop.materials_needed.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={item}
                      onChange={(e) => {
                        const newMaterials = [...editingWorkshop.materials_needed];
                        newMaterials[index] = e.target.value;
                        setEditingWorkshop({ ...editingWorkshop, materials_needed: newMaterials });
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newMaterials = editingWorkshop.materials_needed.filter((_, i) => i !== index);
                        setEditingWorkshop({ ...editingWorkshop, materials_needed: newMaterials });
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingWorkshop({
                      ...editingWorkshop,
                      materials_needed: [...(editingWorkshop.materials_needed || []), '']
                    });
                  }}
                >
                  Add Material
                </Button>
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Notes"
                  value={editingWorkshop.notes || ''}
                  onChange={(e) => setEditingWorkshop({ ...editingWorkshop, notes: e.target.value })}
                />
              </Grid>

              {/* Attendees */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Attendees:</Typography>
                {editingWorkshop.attendees && editingWorkshop.attendees.map((attendee, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      label="Name"
                      value={attendee.name || ''}
                      onChange={(e) => {
                        const newAttendees = [...editingWorkshop.attendees];
                        newAttendees[index] = { ...newAttendees[index], name: e.target.value };
                        setEditingWorkshop({ ...editingWorkshop, attendees: newAttendees });
                      }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Email"
                      value={attendee.email || ''}
                      onChange={(e) => {
                        const newAttendees = [...editingWorkshop.attendees];
                        newAttendees[index] = { ...newAttendees[index], email: e.target.value };
                        setEditingWorkshop({ ...editingWorkshop, attendees: newAttendees });
                      }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Module"
                      value={attendee.module || ''}
                      onChange={(e) => {
                        const newAttendees = [...editingWorkshop.attendees];
                        newAttendees[index] = { ...newAttendees[index], module: e.target.value };
                        setEditingWorkshop({ ...editingWorkshop, attendees: newAttendees });
                      }}
                      sx={{ flex: 0.8 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newAttendees = editingWorkshop.attendees.filter((_, i) => i !== index);
                        setEditingWorkshop({ ...editingWorkshop, attendees: newAttendees });
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingWorkshop({
                      ...editingWorkshop,
                      attendees: [...(editingWorkshop.attendees || []), { name: '', email: '', module: '' }]
                    });
                  }}
                >
                  Add Attendee
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditWorkshopDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveWorkshop}>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Duplicate Scope Items Dialog */}
      <Dialog open={duplicateDialogOpen} onClose={handleSkipDuplicates} maxWidth="md" fullWidth>
        <DialogTitle>Duplicate Scope Items Found</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {pendingDuplicates.length} scope item{pendingDuplicates.length !== 1 ? 's' : ''} already exist in this project.
            Select which ones to replace with the new data from your file, or skip to keep existing data.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
            <Button size="small" onClick={() => setSelectedDuplicateIds(new Set(pendingDuplicates.map(d => d.existing_id)))}>
              Select All
            </Button>
            <Button size="small" onClick={() => setSelectedDuplicateIds(new Set())}>
              Deselect All
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Replace</TableCell>
                  <TableCell>Scope Item</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Existing Description</TableCell>
                  <TableCell>New Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingDuplicates.map((dup) => (
                  <TableRow key={dup.existing_id} selected={selectedDuplicateIds.has(dup.existing_id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedDuplicateIds.has(dup.existing_id)}
                        onChange={() => toggleDuplicateSelection(dup.existing_id)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell><strong>{dup.scope_item}</strong></TableCell>
                    <TableCell>{dup.new_module}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', maxWidth: 200 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {dup.existing_description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {dup.new_description || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSkipDuplicates}>
            Skip All
          </Button>
          <Button
            variant="contained"
            onClick={handleReplaceSelected}
            disabled={selectedDuplicateIds.size === 0}
          >
            Replace Selected ({selectedDuplicateIds.size})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Scope Items Confirmation Dialog */}
      <Dialog
        open={clearAllDialogOpen}
        onClose={() => setClearAllDialogOpen(false)}
      >
        <DialogTitle>Clear All Scope Items?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently delete all {scopeItems.length} scope item{scopeItems.length !== 1 ? 's' : ''} for this project.
          </Alert>
          <Typography>
            All scope item data, workshop mappings, and related configurations will be removed.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClearAll}
            variant="contained"
            color="error"
          >
            Yes, Clear All
          </Button>
        </DialogActions>
      </Dialog>

      <SafetyConfirmDialog
        open={workshopNamesConfirmOpen}
        title={workshopNames.length > 0 ? 'Regenerate Workshops?' : 'Generate Workshops?'}
        warning={workshopNames.length > 0 ? `${workshopNames.length} existing workshop names and mappings may be rebuilt.` : 'This action may use AI to determine the workshop count.'}
        paragraphs={workshopNames.length > 0 ? [
          'This action may use AI and will rebuild the auto-generated workshop names and scope-item mappings.',
          'Any manual changes made to auto-generated workshop names or scope-item mappings will be permanently lost.'
        ] : [
          'This action may use AI to determine the appropriate number of workshops and create workshop mappings from the selected scope items.'
        ]}
        confirmLabel={workshopNames.length > 0 ? 'Yes, Regenerate' : 'Yes, Generate'}
        loading={loading}
        onCancel={() => setWorkshopNamesConfirmOpen(false)}
        onConfirm={() => { setWorkshopNamesConfirmOpen(false); handleAutoGenerateWorkshops(); }}
      />

      <SafetyConfirmDialog
        open={regenerateDialogOpen}
        title={generatedWorkshops.length > 0 ? 'Regenerate Workshop Content?' : 'Generate Workshop Content?'}
        warning={generatedWorkshops.length > 0 ? `${generatedWorkshops.length} existing workshops and their downstream schedule data will be deleted.` : `AI content will be generated for ${workshopNames.length} workshops.`}
        paragraphs={generatedWorkshops.length > 0 ? [
          'This action will use AI to regenerate content for all workshops. Existing workshop content, schedules, calendar invites, and generated Fit-to-Standard presentations will be deleted.',
          'Any manual edits made to the existing workshop content and schedule data will be permanently lost.',
          ...(schedules.length > 0 ? [{ text: 'Download the current workshop schedule as Excel first if you want to retain a backup of the schedule.', secondary: true }] : [])
        ] : [
          'This action will use AI to generate objectives, agendas, materials, notes, and attendees for all workshops. This may take several minutes and consume significant AI resources.'
        ]}
        confirmLabel={generatedWorkshops.length > 0 ? 'Yes, Regenerate All' : 'Yes, Generate All'}
        loading={generating}
        onCancel={() => setRegenerateDialogOpen(false)}
        onConfirm={handleConfirmRegenerate}
      />

      <SafetyConfirmDialog
        open={scheduleConfirmOpen}
        title={schedules.length > 0 ? 'Replace Workshop Schedule?' : 'Generate Workshop Schedule?'}
        warning={schedules.length > 0 ? `${schedules.length} existing schedules will be replaced.` : `A schedule will be generated for ${generatedWorkshops.length} workshops.`}
        paragraphs={schedules.length > 0 ? [
          'This action will replace all existing workshop schedules and remove their calendar invites and generated Fit-to-Standard presentations. New Fit-to-Standard presentations will automatically be generated and may consume significant AI resources.',
          'Any manual edits or uploaded changes made to the existing workshop schedule will be permanently lost.',
          { text: 'Download the current schedule as Excel first if you want to retain your changes.', secondary: true }
        ] : [
          'New Fit-to-Standard presentations will automatically be generated for every scheduled workshop. This may take several minutes and consume significant AI resources.'
        ]}
        confirmLabel="Yes, Generate Schedule"
        loading={scheduling}
        onCancel={() => setScheduleConfirmOpen(false)}
        onConfirm={() => { setScheduleConfirmOpen(false); handleGenerateSchedule(); }}
      />

      {/* Snackbar for notifications */}
      {/* Edit Schedule Dialog */}
      <Dialog open={editScheduleDialogOpen} onClose={handleCloseEditSchedule} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Workshop Schedule</DialogTitle>
        <DialogContent>
          {editingSchedule && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Workshop: {editingSchedule.workshop_name}
              </Typography>

              <TextField
                fullWidth
                label="Date"
                type="date"
                value={editingSchedule.scheduled_date || ''}
                onChange={(e) => setEditingSchedule({ ...editingSchedule, scheduled_date: e.target.value })}
                sx={{ mt: 2 }}
                InputLabelProps={{ shrink: true }}
              />

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Start Time"
                    type="time"
                    value={editingSchedule.start_time || ''}
                    onChange={(e) => setEditingSchedule({ ...editingSchedule, start_time: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="End Time"
                    type="time"
                    value={editingSchedule.end_time || ''}
                    onChange={(e) => setEditingSchedule({ ...editingSchedule, end_time: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditSchedule}>Cancel</Button>
          <Button onClick={handleSaveScheduleEdit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Manual Schedule Dialog */}
      <Dialog open={addScheduleDialogOpen} onClose={handleCloseAddSchedule} maxWidth="sm" fullWidth>
        <DialogTitle>Add Manual Workshop Schedule</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Workshop</InputLabel>
              <Select
                value={newSchedule.workshop_id}
                onChange={(e) => setNewSchedule({ ...newSchedule, workshop_id: e.target.value })}
                label="Workshop"
              >
                {generatedWorkshops.map((workshop) => (
                  <MenuItem key={workshop.id} value={workshop.id}>
                    {workshop.workshop_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Date"
              type="date"
              value={newSchedule.scheduled_date}
              onChange={(e) => setNewSchedule({ ...newSchedule, scheduled_date: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={newSchedule.start_time}
                  onChange={(e) => setNewSchedule({ ...newSchedule, start_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={newSchedule.end_time}
                  onChange={(e) => setNewSchedule({ ...newSchedule, end_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Time Zone</InputLabel>
              <Select
                value={newSchedule.time_zone}
                onChange={(e) => setNewSchedule({ ...newSchedule, time_zone: e.target.value })}
                label="Time Zone"
              >
                {timezoneOptions.map((tz) => (
                  <MenuItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddSchedule}>Cancel</Button>
          <Button onClick={handleSaveManualSchedule} variant="contained" color="primary">
            Add Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Schedule Changes Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleCancelUpload}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '60vh',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Preview Schedule Changes</Typography>
            <Chip
              label={`${previewChanges.length} workshop${previewChanges.length !== 1 ? 's' : ''} will be updated`}
              color={previewChanges.length > 0 ? 'warning' : 'default'}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {previewErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Errors found:</Typography>
              <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                {previewErrors.map((error, index) => (
                  <li key={index}><Typography variant="body2">{error}</Typography></li>
                ))}
              </Box>
            </Alert>
          )}

          {previewChanges.length === 0 ? (
            <Alert severity="info">
              No changes detected. The uploaded file contains the same data as the current schedules.
            </Alert>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                The following changes will be applied when you click "Confirm Upload". Please review carefully.
              </Alert>

              {previewChanges.map((change, index) => (
                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {change.workshop_name}
                      </Typography>
                      <Chip
                        label={`${change.fields_changed.length} field${change.fields_changed.length !== 1 ? 's' : ''} changed`}
                        size="small"
                        color="warning"
                      />
                    </Box>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Current Value</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>New Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {change.fields_changed.map((field, fieldIndex) => (
                            <TableRow key={fieldIndex}>
                              <TableCell>
                                <Chip label={field.field} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    textDecoration: 'line-through',
                                    color: 'text.secondary'
                                  }}
                                >
                                  {field.field === 'Date' ? formatWorkshopDate(field.old_value) :
                                   field.field.includes('Time') ? formatWorkshopTime(field.old_value) :
                                   field.old_value || '(empty)'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 'bold',
                                    color: 'success.main'
                                  }}
                                >
                                  {field.field === 'Date' ? formatWorkshopDate(field.new_value) :
                                   field.field.includes('Time') ? formatWorkshopTime(field.new_value) :
                                   field.new_value || '(empty)'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUpload} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            color="warning"
            disabled={previewChanges.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {loading ? 'Uploading...' : 'Confirm Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkshopSchedulePage;
